<script setup lang="ts">
import { cn } from '@laam/lib/utils';
import {
	SelectContent,
	type SelectContentEmits,
	type SelectContentProps,
	SelectPortal,
	SelectViewport,
	useForwardPropsEmits,
} from 'radix-vue';
import { type HTMLAttributes, computed } from 'vue';
import { SelectScrollDownButton, SelectScrollUpButton } from '.';

defineOptions({
	inheritAttrs: false,
});

const props = withDefaults(
	defineProps<SelectContentProps & { class?: HTMLAttributes['class'] }>(),
	{
		class: '',
		position: 'popper',
	},
);
const emits = defineEmits<SelectContentEmits>();

const delegatedProps = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { class: _, ...delegated } = props;

	return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
	<SelectPortal>
		<SelectContent
			v-bind="{ ...forwarded, ...$attrs }"
			:class="
				cn(
					'min-w-14xl rounded-medium text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-md data-[side=left]:slide-in-from-right-md data-[side=right]:slide-in-from-left-md data-[side=top]:slide-in-from-bottom-md relative z-50 max-h-[24rem] overflow-hidden border border-gray-200 bg-white shadow-md',
					position === 'popper' &&
						'data-[side=bottom]:translate-y-xs data-[side=left]:-translate-x-xs data-[side=right]:translate-x-xs data-[side=top]:-translate-y-xs',
					props.class,
				)
			"
		>
			<SelectScrollUpButton />
			<SelectViewport
				:class="
					cn(
						'p-sm',
						position === 'popper' &&
							'h-[--radix-select-trigger-height] w-full min-w-[--radix-select-trigger-width]',
					)
				"
			>
				<slot />
			</SelectViewport>
			<SelectScrollDownButton />
		</SelectContent>
	</SelectPortal>
</template>
